// eslint-disable no-unsafe-optional-chaining
import type { Choice, Component, RegisterData } from "@/Modules/Models/Domain/OnBoarding";
import type { OnBoardingStore } from "@/Modules/OnBoarding/Store";
import type { UserStore } from "@/Modules/User/Store";
import type { PlanStore } from "@/Modules/Plan/Store";
import { type OnboardingGTMEvent, EventTherapyType } from "@/Modules/App/TrackersAndCookies/GoogleGTM";
import { OnboardingEventTypes, StepId, TherapyType } from "@/Modules/Models/Domain/OnBoarding";
import { EventPlatform } from "@/Modules/App/TrackersAndCookies/GoogleGTM";
import { addGtmEvent } from "@/Modules/App/TrackersAndCookies/GoogleGTM";

import { useUserStore } from "@/Modules/User/Store";
import { usePlanStore } from "@/Modules/Plan/Store";

import { isValidString } from "@/Utils/String";
import { isAndroidApp, isApp, isIosApp } from "@/Utils/Ionic";
import { hasValue } from "@/Utils/Objects";

class SetGTMEventActionHandler {
  constructor(
    private readonly onBoardingStore: OnBoardingStore,
    private readonly userStore: UserStore = useUserStore(),
    private readonly planStore: PlanStore = usePlanStore()
  ) {}

  public handle(data: {
    language: string;
    component?: Component;
    registerData?: RegisterData;
    eventStep?: string;
    planId?: string;
  }) {
    const { language, component, registerData, eventStep, planId } = data;

    const getPlatform = (): EventPlatform => {
      let platform = EventPlatform.WEB;

      if (isApp()) {
        if (isAndroidApp()) {
          platform = EventPlatform.ANDROID;
        } else if (isIosApp()) {
          platform = EventPlatform.IOS;
        }
      }

      return platform;
    };

    const selectedPlan = () => {
      if (this.onBoardingStore.currentStep === StepId.CHECKOUT) {
        return planId;
      } else {
        return undefined;
      }
    };

    const userType = () => {
      if (this.onBoardingStore.currentStep === StepId.CHECKOUT) {
        return this.userStore.user?.isB2b ? "B2B" : "B2C";
      } else {
        return undefined;
      }
    };

    const generateUUID = () => {
      let d = new Date().getTime(),
        d2 = (typeof performance !== "undefined" && performance.now && performance.now() * 1000) || 0;
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        let r = Math.random() * 16;
        if (d > 0) {
          r = (d + r) % 16 | 0;
          d = Math.floor(d / 16);
        } else {
          r = (d2 + r) % 16 | 0;
          d2 = Math.floor(d2 / 16);
        }
        return (c === "x" ? r : (r & 0x7) | 0x8).toString(16);
      });
    };

    if (!isValidString(this.onBoardingStore.sessionUuid)) {
      this.onBoardingStore.sessionUuid = generateUUID();
    }

    const platform = getPlatform();

    const getTherapyTypeUser = () => {
      return this.userStore.user?.isUnder18
        ? EventTherapyType.UNDER_18
        : this.userStore.user?.isCouple
          ? EventTherapyType.COUPLE
          : EventTherapyType.INDIVIDUAL;
    };

    const getTypePlan = () => {
      const plan = isValidString(planId) ? this.planStore.getPlanById(planId) : undefined;

      return hasValue(plan) ? plan.videoCalls : undefined;
    };

    const gtmEvent: OnboardingGTMEvent = {
      event_step: eventStep ? eventStep : OnboardingEventTypes[this.onBoardingStore.currentStep],
      tc_country: language,
      rn_id: this.onBoardingStore.sessionUuid,
      tc_user_type: this.onBoardingStore.isGtmReturning ? "returning" : "new",
      step_value: this.setGTMValue(component, registerData),
      tc_acquisition_user_type: userType(),
      tc_plan_selected: selectedPlan(),
      uuid: this.userStore.user?.id,
      tc_platform: platform,
      tc_test_version: this.planStore.getTestVersion,
      tc_therapy_type: getTherapyTypeUser(),
      tc_type_plan_selected: getTypePlan(),
    };

    addGtmEvent(gtmEvent);
  }
  private setGTMValue = (component?: Component, registerData?: RegisterData): string | undefined => {
    const answer = component?.value;

    switch (this.onBoardingStore.currentStep) {
      case StepId.THERAPY_TYPE: {
        if (answer === TherapyType.SINGLE) return EventTherapyType.INDIVIDUAL;
        else if (answer === TherapyType.COUPLE) return EventTherapyType.COUPLE;
        else if (answer === TherapyType.MINOR) return EventTherapyType.UNDER_18;
        else return answer as string;
      }

      case StepId.USER_AGE:
      case StepId.USER_COUPLE_AGE:
      case StepId.COUPLE_AGE:
      case StepId.USER_AGE_MINOR:
        return answer as string;

      case StepId.USER_GENDER:
      case StepId.USER_COUPLE_GENDER:
      case StepId.COUPLE_GENDER:
        return answer === "nonbinary"
          ? "non-binary"
          : answer === "no_answer"
            ? "not_to_answer"
            : (answer as string);

      case StepId.MAIN_AREA: {
        const answerName = (component?.choices as Choice[]).find((choice) => choice.uuid === answer)?.name;
        return answerName === "couple" ? "relationship_issues" : answerName;
      }

      case StepId.SUB_AREA:
      case StepId.COUPLE_SUBAREA: {
        return (component?.choices as Choice[]).find((choice) => choice.uuid === answer)?.name;
      }

      case StepId.TELL_ME_MORE:
        return `${isValidString(answer)}`;

      case StepId.THERAPIST_GENDER_PREFERENCES: {
        const answerName =
          (component?.choices as Choice[]).find((choice) => choice.uuid === answer)?.i18n ?? "";

        return ["male", "female"].includes(answerName) ? answerName : "no_preference";
      }

      case StepId.THERAPIST_AVAILABILITIES: {
        const answerName =
          (component?.choices as Choice[]).find((choice) => choice.uuid === answer)?.i18n ?? "";

        return answerName.replace("afternoons", "evenings").replace("_and_", "_");
      }

      case StepId.REGISTER: {
        // 1 = acepta todo
        if (!registerData?.isWantTrial && registerData?.newsLetter) {
          return "1";
        }
        // 2 = acepta todo menos newsletter
        if (!registerData?.isWantTrial) {
          return "2";
        }
        // 3 = acepta todo menos prueba gratuita
        if (registerData?.newsLetter) {
          return "3";
        }

        //0 = solo acepta términos y condiciones + política de privacidad
        return "0";
      }

      case StepId.CHECKOUT: {
        return this.onBoardingStore.isTrial ? "1" : "0";
      }

      default:
        return undefined;
    }
  };
}

export default SetGTMEventActionHandler;
